import Alpine from "alpinejs";

export function initBundleBuilderComponent() {
  Alpine.data(
    "bundleBuilder",
    ({ tieredPricingArray, discountPercent }: { tieredPricingArray: TieredPricing[]; discountPercent: number }) => ({
      init() {
        this.steps = Array.from(this.$el.querySelectorAll<HTMLDivElement>("[data-step]")).map((step, index) => ({
          title: step.dataset.step!,
          active: index === 0,
        }));

        this.variants = Array.from(this.$el.querySelectorAll<HTMLDivElement>("[data-variant]")).map((variant) => ({
          id: Number(variant.dataset.id!),
          name: variant.dataset.name!,
          imgSrc: variant.dataset.imgSrc!,
          price: Number(variant.dataset.price!),
          compareAtPrice: Number(variant.dataset.compareAtPrice!),
        }));
      },

      steps: [] as Step[],
      variants: [] as Variant[],

      /** Selection */
      fields: {} as Record<string, number>,
      get selectedVariants() {
        return Object.entries(this.fields)
          .filter(([, value]: [string, number]) => value > 0)
          .map(([key]) => this.variants.find((variant: Variant) => variant.id === +key))
          .filter((variant) => !!variant)
          .map((variant) => ({ ...variant, quantity: this.fields[variant.id] }));
      },

      /** Subscription Selector */
      discountPercent,
      purchaseOption: "one-time",

      /** Pricing Tiers */
      tiers: tieredPricingArray
        .sort((a, b) => a.activation_threshold - b.activation_threshold)
        .map((d) => ({ ...d, discount_amount: +(d.reward.split("%")[0] || 0) / 100 })),
      get builderSubtotal() {
        return this.selectedVariants.reduce((acc, variant) => acc + variant.price * variant.quantity, 0);
      },
      get builderDiscountableSubtotal() {
        return this.selectedVariants
          .filter((variant) => !variant.compareAtPrice)
          .reduce((acc, variant) => acc + variant.price * variant.quantity, 0);
      },
      get computedSubtotal() {
        return Alpine.store("cart").original_total_price + this.builderSubtotal;
      },
      get discountAmount() {
        return (this.currentTier?.discount_amount || 0) * this.builderDiscountableSubtotal;
      },
      get builderTotal() {
        return this.builderSubtotal - this.discountAmount;
      },
      get total() {
        return this.builderTotal * (1 - (this.purchaseOption === "subscription" ? discountPercent : 0));
      },
      get activeTiers() {
        return this.tiers.filter((tier: TieredPricing) => tier.activation_threshold <= this.computedSubtotal);
      },
      get currentTier() {
        return this.activeTiers[this.activeTiers.length - 1];
      },
      get nextTier() {
        return this.tiers.find((tier: TieredPricing) => tier.activation_threshold > this.computedSubtotal);
      },
    }),
  );
}

/** Types */

export interface Step {
  title: string;
  active: boolean;
}

export interface Product {
  id: number;
  title: string;
  handle: string;
  price: number;
  variants: Variant[];
}

export interface Variant {
  id: number;
  name: string;
  imgSrc: string;
  price: number;
  compareAtPrice: number;
}

export interface ImageDetails {
  id: number;
  product_id: number;
  position: number;
  created_at: string;
  updated_at: string;
  alt: string;
  width: number;
  height: number;
  src: string;
  variant_ids: number[];
}

export interface SellingPlanAllocation {
  price_adjustments: SellingPlanAllocationPriceAdjustment[];
  price: number;
  compare_at_price: number;
  per_delivery_price: number;
  selling_plan_id: number;
  selling_plan_group_id: string;
}

export interface SellingPlanAllocationPriceAdjustment {
  position: number;
  price: number;
}

export interface QuantityRule {
  min: number;
  max: number | null;
  increment: number;
}

export interface Media {
  alt: string | null;
  id: number;
  position: number;
  preview_image: {
    aspect_ratio: number;
    height: number;
    width: number;
    src: string;
  };
  aspect_ratio: number;
  height: number;
  media_type: string;
  src: string;
  width: number;
}

export interface SellingPlanGroup {
  id: string;
  name: string;
  options: SellingPlanGroupOption[];
  selling_plans: SellingPlan[];
  app_id: string;
}

export interface SellingPlanGroupOption {
  name: string;
  position: number;
  values: string[];
}

export interface SellingPlan {
  id: number;
  name: string;
  description: string | null;
  options: SellingPlanOption[];
  recurring_deliveries: boolean;
  price_adjustments: SellingPlanPriceAdjustment[];
  checkout_charge: CheckoutCharge;
}

export interface SellingPlanOption {
  name: string;
  position: number;
  value: string;
}

export interface SellingPlanPriceAdjustment {
  order_count: number | null;
  position: number;
  value_type: string;
  value: number;
}

export interface CheckoutCharge {
  value_type: string;
  value: number;
}

export interface TieredPricing {
  activation_threshold: number;
  code: string;
  market: string;
  reward: string;
}
